import { Box, type SxProps, type Theme, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";
import type { FC, ReactNode } from "react";
import { FONT_SIZE, MOTION } from "../../../../theme/tokens";
import { InHouseTooltip } from "../../../InHouseTooltip/InHouseTooltip";

/** One threshold along the bar's axis. */
export type ProgressMilestone = {
	/** Stable key for the marker. */
	id: string;
	/** Position along the axis, 0–100. Markers outside that range are not rendered. */
	pct: number;
	/** Already-translated primary label under the marker (e.g. "+10 pts"). */
	label: ReactNode;
	/** Already-translated secondary label (e.g. the value the marker sits at). */
	sublabel?: ReactNode;
	/** Whether the bar has passed this threshold. Drives the marker's emphasis. */
	achieved: boolean;
	/** Optional hover text explaining what crossing this threshold earns. */
	tooltip?: string;
	/**
	 * What crossing this threshold MEANS. `gain` (default) is a reward: muted until reached, success once
	 * crossed. `risk` is a loss to stay clear of (a band floor a cut must not fall through): warning while
	 * still ahead of the fill, error once crossed — so a crossed risk never reads as an achievement.
	 */
	tone?: MilestoneTone;
	/**
	 * Already-translated words for the marker's state ("Crossed — points lost", "Not reached yet"),
	 * rendered as screen-reader text beside the label. The ticks sit inside the `progressbar`, whose
	 * children assistive tech treats as presentational, so the state has to be said in the label row.
	 */
	stateLabel?: string;
};

export type MilestoneTone = "gain" | "risk";

// Tick fill for a marker. A crossed `gain` tick is cut out of the fill in the paper colour; a `risk`
// tick stays coloured in both states so it reads as a warning line, not a notch in delivered ground.
const milestoneTickColor = (theme: Theme, milestone: ProgressMilestone): string => {
	if (milestone.tone === "risk") return milestone.achieved ? theme.palette.error.main : theme.palette.warning.main;
	return milestone.achieved ? theme.palette.background.paper : alpha(theme.palette.text.primary, 0.28);
};

// A crossed `risk` tick overhangs the track, so "fallen through this floor" reads by SHAPE as well as
// by hue — warning-amber against error-red is the pair colour-blind readers lose first.
const CROSSED_RISK_OVERHANG_PX = 3;
const isCrossedRisk = (milestone: ProgressMilestone): boolean => milestone.tone === "risk" && milestone.achieved;

const milestoneLabelColor = (theme: Theme, milestone: ProgressMilestone): string => {
	if (milestone.tone === "risk") return milestone.achieved ? theme.palette.error.dark : theme.palette.warning.dark;
	return milestone.achieved ? theme.palette.success.dark : theme.palette.text.secondary;
};

/**
 * Where the bar is expected to END UP, drawn ahead of the current fill as a translucent band closed
 * by a line.
 *
 * Kept visually distinct from the fill on purpose: the fill is measured, this is a forecast, and a
 * reader who cannot tell the two apart has been told a guess as a fact.
 */
export type ProgressProjection = {
	/** Position along the axis, 0–100. Clamped like `value`. */
	pct: number;
	/** Already-translated label for the marker (e.g. "Projected 1,410 hrs"). */
	label: ReactNode;
	/** Whether the projection reaches the target the bar fills toward. Drives the marker's emphasis. */
	reachesTarget: boolean;
	/** Optional hover text explaining how the projection is derived. */
	tooltip?: string;
};

type Props = {
	/** Fill, 0–100. Values outside are clamped — the caller owns the real (unclamped) number. */
	value: number;
	/** Thresholds to mark along the axis. Order is irrelevant; each is positioned by `pct`. */
	milestones?: ProgressMilestone[];
	/** Optional forecast of where the fill is heading. Omit when there is no basis for one. */
	projection?: ProgressProjection;
	/**
	 * Already-formatted label pinned to the END of the fill, so the current value reads against the
	 * coloured line itself rather than only in a caption underneath.
	 */
	valueLabel?: ReactNode;
	/** Fill tone. Defaults to `primary`; use `warning` to signal "behind" without changing layout. */
	tone?: "primary" | "success" | "warning" | "error";
	/** Track thickness in px. Default 12. */
	height?: number;
	/** Accessible description of what the bar measures. */
	ariaLabel?: string;
	/** Layout overrides — the caller owns width/margins. */
	sx?: SxProps<Theme>;
	dataTestId?: string;
};

const clampPct = (value: number): number => Math.min(100, Math.max(0, value));

// Minimum gap between two marker labels before the second is dropped, as a share of the axis.
const MIN_LABEL_GAP_PCT = 12;

// Height of the projection label's row. The projection line rises by exactly this much so it lands on
// the chip above it; the two are one marker, and a gap between them would read as two separate marks.
const PROJECTION_LABEL_ROW_PX = 20;

/**
 * A horizontal progress bar with labelled threshold markers along its axis.
 *
 * The point of the markers is that progress toward the thresholds is NOT linear in the reward:
 * they are rendered as discrete crossings, and nothing is interpolated between them. Use for any
 * "progress toward a target, with graded checkpoints on the way" surface — a CMS band ladder, a
 * tiered bonus, a completion streak.
 *
 * An optional `projection` draws where the fill is HEADING — a translucent band from the fill to the
 * forecast, closed by a line, labelled on its own row above. Rendered unmistakably differently from
 * the fill because one is measured and the other is a forecast.
 *
 * Purely presentational: the caller computes every `pct` (including where a marker sits relative
 * to the goal) and decides what `achieved` means, so the bar can never disagree with the numbers
 * printed beside it. For a plain bar with no thresholds use MUI `<LinearProgress>`; for the
 * 8-segment occupancy read use `SegmentedProgressBar`.
 */
export const MilestoneProgressBar: FC<Props> = ({
	value,
	milestones = [],
	projection,
	valueLabel,
	tone = "primary",
	height = 12,
	ariaLabel,
	sx,
	dataTestId,
}) => {
	const fill = clampPct(value);
	const projectedPct = projection ? clampPct(projection.pct) : 0;
	// Ordered rather than assumed to run forward: a lever delivering below its own baseline clamps
	// both ends to zero, and a band with a negative width would paint outside the track.
	const bandStart = Math.min(fill, projectedPct);
	const bandWidth = Math.abs(projectedPct - fill);
	// Keep the label's anchor off the extremes so a 0% or 100% fill doesn't half-hang outside the
	// track. Only the label moves — the fill itself still reflects the true value.
	const labelAnchor = Math.min(92, Math.max(8, fill));
	// Wider inset than the fill's label: this one carries a padded chip, so it needs more room before
	// it starts clipping at either end of the track.
	const projectionAnchor = Math.min(86, Math.max(14, projectedPct));
	// Only markers that actually land on the axis are drawn — one past the goal would otherwise
	// pile up on the right edge and read as "reached".
	const visible = milestones.filter((m) => m.pct >= 0 && m.pct <= 100);
	// Every marker keeps its tick, but a label is only drawn when it clears the last one it would
	// have overlapped. Band edges cluster hard at the low end of a large commitment — several
	// within a percent or two of each other — and their two-line labels then pile into an
	// unreadable smear at the left. Ticks stay dense; the text thins out.
	const labelled = new Set<string>();
	let lastLabelPct = Number.NEGATIVE_INFINITY;
	for (const milestone of [...visible].sort((a, b) => a.pct - b.pct)) {
		if (milestone.pct - lastLabelPct < MIN_LABEL_GAP_PCT) continue;
		labelled.add(milestone.id);
		lastLabelPct = milestone.pct;
	}

	return (
		<Box sx={[{ width: "100%" }, ...(sx === undefined ? [] : Array.isArray(sx) ? sx : [sx])]} data-testid={dataTestId}>
			{valueLabel && (
				<Box sx={{ position: "relative", height: 20, mb: 0.25 }}>
					<Typography
						component="span"
						variant="caption"
						data-testid={dataTestId ? `${dataTestId}-value` : undefined}
						sx={(theme) => ({
							position: "absolute",
							insetInlineStart: `${labelAnchor}%`,
							transform: "translateX(-50%)",
							whiteSpace: "nowrap",
							fontWeight: 700,
							color: theme.palette[tone].dark,
						})}
					>
						{valueLabel}
					</Typography>
				</Box>
			)}
			{/* Sits immediately above the track, below the fill's own label, so the line rising off the
			    bar reaches it and the two read as one marker. Its own row rather than sharing the fill's:
			    early on, the forecast sits almost on top of the current value, and two labels on one line
			    would collide exactly when the reader most wants to compare them. */}
			{projection && (
				<Box sx={{ position: "relative", height: PROJECTION_LABEL_ROW_PX }}>
					<InHouseTooltip title={projection.tooltip ?? ""}>
						<Typography
							component="span"
							data-testid={dataTestId ? `${dataTestId}-projection-label` : undefined}
							sx={(theme) => ({
								position: "absolute",
								insetInlineStart: `${projectionAnchor}%`,
								transform: "translateX(-50%)",
								whiteSpace: "nowrap",
								fontSize: FONT_SIZE.LABEL_XS,
								fontWeight: 700,
								px: 0.75,
								py: 0.25,
								borderRadius: 0.75,
								cursor: projection.tooltip ? "help" : "default",
								color: projection.reachesTarget ? theme.palette.success.dark : theme.palette.text.secondary,
								backgroundColor: alpha(projection.reachesTarget ? theme.palette.success.main : theme.palette.text.primary, 0.12),
							})}
						>
							{projection.label}
						</Typography>
					</InHouseTooltip>
					{/* Decorative tick tying the chip to the line on the track. Explicitly non-interactive:
					    it overlaps the chip, and a bare 2px div capturing the hover would swallow the
					    tooltip of the labelled thing it is only there to point at. */}
					<Box
						aria-hidden
						sx={(theme) => ({
							position: "absolute",
							insetInlineStart: `${projectedPct}%`,
							bottom: 0,
							height: PROJECTION_LABEL_ROW_PX / 2,
							width: 2,
							transform: "translateX(-50%)",
							pointerEvents: "none",
							backgroundColor: projection.reachesTarget ? theme.palette.success.main : theme.palette.text.secondary,
						})}
					/>
				</Box>
			)}
			<Box
				role="progressbar"
				aria-valuenow={Math.round(fill)}
				aria-valuemin={0}
				aria-valuemax={100}
				aria-label={ariaLabel}
				sx={(theme) => ({
					position: "relative",
					height,
					borderRadius: height / 2,
					backgroundColor: alpha(theme.palette.text.primary, 0.08),
					overflow: "visible",
				})}
			>
				<Box
					sx={(theme) => ({
						position: "absolute",
						insetInlineStart: 0,
						top: 0,
						height: "100%",
						width: `${fill}%`,
						borderRadius: height / 2,
						backgroundColor: theme.palette[tone].main,
						transition: `width ${MOTION.DURATION.VIEW_ENTER}ms ${MOTION.EASE_OUT}`,
						"@media (prefers-reduced-motion: reduce)": { transition: "none" },
					})}
				/>
				{/* The gap the forecast expects to close, dashed and translucent so it never reads as
				    delivered ground. Drawn before the markers so their ticks stay legible over it. */}
				{projection && (
					<Box
						data-testid={dataTestId ? `${dataTestId}-projection-band` : undefined}
						sx={(theme) => ({
							position: "absolute",
							insetInlineStart: `${bandStart}%`,
							top: 0,
							height: "100%",
							width: `${bandWidth}%`,
							backgroundColor: alpha(theme.palette.text.primary, 0.12),
							borderBlock: `1px dashed ${alpha(theme.palette.text.primary, 0.4)}`,
							transition: `width ${MOTION.DURATION.VIEW_ENTER}ms ${MOTION.EASE_OUT}`,
							"@media (prefers-reduced-motion: reduce)": { transition: "none" },
						})}
					/>
				)}
				{/* The line the band closes on, spanning the track only — the tick that reaches up to the
				    chip is drawn in the label row above. Hoverable in its own right: the line is the mark a
				    reader points at, so leaving only the chip to explain itself would strand it. */}
				{projection && (
					<InHouseTooltip title={projection.tooltip ?? ""}>
						<Box
							data-testid={dataTestId ? `${dataTestId}-projection-line` : undefined}
							data-reaches-target={projection.reachesTarget}
							sx={(theme) => ({
								position: "absolute",
								insetInlineStart: `${projectedPct}%`,
								top: 0,
								height: "100%",
								width: 2,
								transform: "translateX(-50%)",
								borderRadius: 1,
								zIndex: 1,
								cursor: projection.tooltip ? "help" : "default",
								backgroundColor: projection.reachesTarget ? theme.palette.success.main : theme.palette.text.secondary,
							})}
						/>
					</InHouseTooltip>
				)}
				{visible.map((milestone) => (
					<InHouseTooltip key={milestone.id} title={milestone.tooltip ?? ""}>
						<Box
							data-testid={dataTestId ? `${dataTestId}-milestone-${milestone.id}` : undefined}
							data-achieved={milestone.achieved}
							data-tone={milestone.tone === "risk" ? "risk" : "gain"}
							sx={(theme) => ({
								position: "absolute",
								insetInlineStart: `${milestone.pct}%`,
								top: isCrossedRisk(milestone) ? -CROSSED_RISK_OVERHANG_PX : 0,
								bottom: isCrossedRisk(milestone) ? -CROSSED_RISK_OVERHANG_PX : 0,
								width: 2,
								transform: "translateX(-50%)",
								borderRadius: 1,
								backgroundColor: milestoneTickColor(theme, milestone),
								cursor: milestone.tooltip ? "help" : "default",
							})}
						/>
					</InHouseTooltip>
				))}
			</Box>
			{visible.length > 0 && (
				<Box sx={{ position: "relative", height: 30, mt: 0.5 }}>
					{visible
						.filter((milestone) => labelled.has(milestone.id))
						.map((milestone) => (
							<Box
								key={milestone.id}
								sx={{
									position: "absolute",
									insetInlineStart: `${milestone.pct}%`,
									transform: "translateX(-50%)",
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									lineHeight: 1.25,
									whiteSpace: "nowrap",
								}}
							>
								<Typography
									component="span"
									sx={(theme) => ({
										fontSize: FONT_SIZE.LABEL_XS,
										fontWeight: 700,
										color: milestoneLabelColor(theme, milestone),
									})}
								>
									{milestone.label}
									{milestone.stateLabel && <Box component="span" sx={visuallyHidden}>{` (${milestone.stateLabel})`}</Box>}
								</Typography>
								{milestone.sublabel && (
									<Typography component="span" sx={{ fontSize: FONT_SIZE.LABEL_XS, color: "text.disabled" }}>
										{milestone.sublabel}
									</Typography>
								)}
							</Box>
						))}
				</Box>
			)}
		</Box>
	);
};

export default MilestoneProgressBar;
